import { useState, useEffect } from "react";
import {
  CloseButton,
  StatusMessage,
} from "../../style/view.styles";
import { fetchEntityData, saveEntityData } from "../../utils/RequestHandler";
import { getLoggedInUser } from "../../utils/AuthHandler";
import LoadingSpinner from "./LoadingSpinner";
import {
  FormContainer,
  StyledForm,
  StyledFormField,
  StyledLabel,
  StyledTextarea,
  Title,
} from "../../style/modal.form.style";
import { FaPen } from "react-icons/fa";
import FullButton from "../Buttons/FullButton";

const InternshipApplicationForm = ({
  selectedInternship,
  handleModalClose,
  setIsApplicationSubmitted,
}) => {
  const [loading, setLoading] = useState(true);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [talentId, setTalentId] = useState(null);
  const [organizationId, setOrganizationId] = useState(null);
  const [formStatus, setFormStatus] = useState({ message: "", type: "" });
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    cover_letter: "",
  });

  useEffect(() => {
    const setDefaultStatus = async () => {
      try {
        const response = await fetchEntityData("application_status");
        if (response.success) {
          setApplicationStatus(
            response.result.find((item) => item.status === "new").id
          );
        }
      } catch (error) {
        console.error(error);
      }
    };

    const setOrganization = async () => {
      try {
        const response = await fetchEntityData("organizations");
        if (response.success) {
          setOrganizationId(
            response.result.find(
              (item) => item.name === selectedInternship.organization
            ).id
          );
        }
      } catch (error) {
        console.error(error);
      }
    };

    const setTalent = async () => {
      try {
        const user = getLoggedInUser();
        const response = await fetchEntityData("talents", true);
        if (!response?.success || !Array.isArray(response.result)) {
          throw new Error("Invalid response format or request failed");
        }
        const data = response.result.find(
          (record) => record.email === user.username
        );
        setTalentId(data.id);
      } catch (error) {
        console.error("Error fetching talent profile:", error);
      } finally {
        setLoading(false);
      }
    };

    setDefaultStatus();
    setOrganization();
    setTalent();
  }, [selectedInternship.organization]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFormSubmit = async () => {
    setSubmitting(true);
    try {
      const requestData = {
        ...formData,
        applicant: talentId,
        internship: selectedInternship.id,
        organization: organizationId,
        status: applicationStatus,
      };
      const response = await saveEntityData(
        "internship_applications",
        requestData
      );
      if (response.success) {
        setFormStatus({
          message: "Application sent successfully!",
          type: "success",
        });
        setSubmitting(false);
        setIsApplicationSubmitted(true);
        handleModalClose();
      } else {
        setFormStatus({
          message:
            response.message ??
            "An error occurred while submitting your request. Please try again.",
          type: "error",
        });
      }
    } catch (error) {
      console.error(error);
      setFormStatus({
        message:
          "An error occurred while submitting your application. Please try again.",
        type: "error",
      });
    }
  };

  return (
      <FormContainer>
        <CloseButton onClick={handleModalClose}>X</CloseButton>
        <Title>Apply for {selectedInternship?.position}</Title>
        <StyledForm>
          {loading && <LoadingSpinner />}
          {!loading && (
            <>
              <StyledFormField fullWidth>
                <StyledLabel>
                  <FaPen style={{ marginRight: 5 }} /> Cover letter
                </StyledLabel>
                <StyledTextarea
                  name="cover_letter"
                  placeholder="Cover letter"
                  rows="4"
                  value={formData.cover_letter}
                  onChange={handleInputChange}
                  required
                />
              </StyledFormField>
            </>
          )}
        </StyledForm>
        <FullButton
          action={handleFormSubmit}
          type="submit"
          disabled={submitting}
          title={submitting ? "Submitting..." : "Submit Application"}
        />
        {formStatus.message && (
          <StatusMessage type={formStatus.type}>
            {formStatus.message}
          </StatusMessage>
        )}
      </FormContainer>
  );
};

export default InternshipApplicationForm;
